import React from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { Link as RouterLink } from "react-router";
import { employeeDashboardOrder, sectionOrder } from "../utils/consts";
import {
  countDashboardsWithChildren,
  formatDashboardCountLabel,
} from "../utils/dashboardCount";
import { getDashboardNavigationAccordionStyles } from "../styles/drawer";
import DashboardChildNavList from "./DashboardChildNavList";
import type { DashboardInfo } from "../utils/types";
import { useApp } from "../contexts/AppContext";

interface DashboardNavigationAccordionProps {
  readonly dashboardList: any[];
  readonly onDashboardClick?: () => void;
}

export default function DashboardNavigationAccordion({
  dashboardList,
  onDashboardClick,
}: DashboardNavigationAccordionProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const styles = getDashboardNavigationAccordionStyles(theme);
  const { handleDrawerClose } = useApp();
  const toggleSection =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleDashboardNavigate = () => {
    onDashboardClick?.();
    handleDrawerClose();
  };

  const dashboardsBySection: any = dashboardList.reduce(
    (acc: any, dashboard: any) => {
      acc[dashboard.section] ??= [];
      acc[dashboard.section].push(dashboard);
      return acc;
    },
    {},
  );

  type EmployeeDashboardOrderKey = keyof typeof employeeDashboardOrder;

  Object.keys(dashboardsBySection).forEach((section) => {
    dashboardsBySection[section].sort((a: any, b: any) => {
      if (section === "Employee Dashboard") {
        const orderA =
          employeeDashboardOrder[a.title as EmployeeDashboardOrderKey] ?? 999;
        const orderB =
          employeeDashboardOrder[b.title as EmployeeDashboardOrderKey] ?? 999;
        return orderA - orderB;
      }
      const idxA = sectionOrder.indexOf(a.title as string);
      const idxB = sectionOrder.indexOf(b.title as string);
      return (
        (idxA === -1 ? sectionOrder.length : idxA) -
        (idxB === -1 ? sectionOrder.length : idxB)
      );
    });
  });

  const renderDashboardButton = (dashboard: any) => {
    return (
      <Button
        component={RouterLink}
        to={`/dashboard/${dashboard.id}`}
        variant="text"
        key={dashboard.id}
        onClick={handleDashboardNavigate}
        sx={{ ...styles.dashboardButton, textDecoration: "none" }}
      >
        <Typography sx={styles.dashboardButtonTitle}>
          {dashboard.title}
        </Typography>
      </Button>
    );
  };

  const sortedSections = Object.entries(dashboardsBySection).sort(
    ([sectionA], [sectionB]) => {
      const idxA = sectionOrder.indexOf(sectionA);
      const idxB = sectionOrder.indexOf(sectionB);
      return (
        (idxA === -1 ? sectionOrder.length : idxA) -
        (idxB === -1 ? sectionOrder.length : idxB)
      );
    },
  );

  return (
    <Box sx={styles.rootBox}>
      {sortedSections.map((sectionData, idx) => {
        const [section, sectionDashboards] = sectionData as [string, any[]];
        const sectionDashboardCount = countDashboardsWithChildren(sectionDashboards);

        return (
          <Accordion
            key={section}
            expanded={expanded === `panel${idx}`}
            onChange={toggleSection(`panel${idx}`)}
            disableGutters={true}
            sx={styles.accordion}
          >
            <AccordionSummary
              expandIcon={
                expanded === `panel${idx}` ? (
                  <img src="/images/up-arrow.svg" alt="less" />
                ) : (
                  <img src="/images/down-arrow.svg" alt="more" />
                )
              }
              aria-controls={`panel${idx}bh-content`}
              id={`panel${idx}bh-header`}
              sx={styles.accordionSummary}
            >
              <Box sx={styles.accordionSummaryContent}>
                <Typography sx={styles.sectionTitle} variant="h6">
                  {section}
                </Typography>
                <Typography sx={styles.sectionSubtitle} variant="subtitle1">
                  {formatDashboardCountLabel(sectionDashboardCount)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={styles.accordionDetails}>
              {sectionDashboards.map((dashboard: DashboardInfo) => (
                <div key={dashboard.id}>
                  <Box sx={styles.divider} />
                  {renderDashboardButton(dashboard)}
                  <DashboardChildNavList
                    childDashboards={dashboard.child_dashboards}
                    parentSection={dashboard.section}
                    onNavigate={handleDashboardNavigate}
                  />
                </div>
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
